import React, { useCallback, useEffect } from "react";
import { Button, Collapse, theme, Typography } from "antd";
import "./FiltersPanel.css";
import { ConceptsFilterContent } from "./ConceptsFilterContent";
import { DatesFilterContent } from "./DatesFilterContent";
import { ValuesFilterContent } from "./ValuesFilterContent";
import { CollapseIcon, ExpandIcon } from "../../../icons";
import { useParams } from "react-router-dom";
import {
    useGetConceptMetaDataQuery,
    useGetConceptsListQuery,
} from "../../../store/api/lumi/concepts";
import { useDispatch, useSelector } from "react-redux";
import { selectDashboardById } from "../../../store/selectors/selectors";
import {
    resetSelections,
    setActiveConceptList,
    setDeselection,
    setDeselectionConcepts,
    setDeselectionDate,
    setSelections,
    setTopConceptList,
    setTopConceptListDaylight,
    setTopConcepts,
    setTopFilters,
} from "../../../store/slices/dashboardSlice";

const { Text, Title } = Typography;

export const FiltersPanel = () => {
    const { token } = theme.useToken();
    const { id, dashboardName: nameFromParams } = useParams();
    const { data: dataList, isLoading } = useGetConceptsListQuery(id);
    const { data: metadataResponse, isSuccess } =
        useGetConceptMetaDataQuery(id);

    const dispatch = useDispatch();

    const {
        topFilters = [],
        selections = {},
        topConcepts = [],
        activeConceptList = "",
    } = useSelector((state) => selectDashboardById(state, id));

    const { topOptionsConcepts, sentimentDisabled } = useSelector(
        (state) => state.project
    );
    console.log("topOptionsConcepts=>", topOptionsConcepts)

    const dispatchSetFilters = useCallback(
        (filterData) => {
            dispatch(setTopFilters({ id, filters: [filterData] }));
        },
        [dispatch, id]
    );

    const dispatchSetSelections = useCallback(
        (selectionData) => {
            dispatch(setSelections({ id, selections: selectionData }));
        },
        [dispatch, id]
    );

    const dispatchSetTopConcepts = useCallback(
        (concepts) => {
            dispatch(setTopConcepts({ id, concepts }));
        },
        [dispatch, id]
    );

    const handleConceptsChange = useCallback(
        (newConcepts) => {
            dispatchSetTopConcepts(newConcepts);
        },
        [dispatchSetTopConcepts]
    );

    const dispatchSetDeselection = useCallback(
        (deselection) => {
            dispatch(setDeselection({ id, deselection }));
        },
        [dispatch, id]
    );

    const dispatchSetDeselectionConcepts = (deselectionConcepts) => {
        dispatch(setDeselectionConcepts({ id, deselectionConcepts }));
    };
    const handleConceptListChange = useCallback(
        (list) => {
            dispatch(setTopConceptList({ id, list }));
            dispatch(setActiveConceptList({ id, list }));
        },
        [dispatch, id]
    );

    const handleChangeConceptListType = useCallback(
        (type) => {
            dispatch(setTopConceptListDaylight({ id, daylight: type }));
            dispatch(setActiveConceptList({ id, list: `Type: ${type}` }));
        },
        [dispatch, id]
    );

    const handleFilter = useCallback(
        (item, newValue) => {
            const previousValues = selections[item.name]?.values || [];
            const deselectedValues = previousValues.filter(
                (x) => !newValue.includes(x)
            );

            if (deselectedValues.length > 0) {
                dispatchSetDeselection({
                    name: item.name,
                    values: deselectedValues,
                    type: item.type,
                });
            }

            dispatchSetSelections({
                [item.name]: {
                    type: item.type,
                    values: newValue,
                },
            });
        },
        [dispatchSetSelections, selections, dispatchSetDeselection]
    );

    const dispatchSetDeselectionDate = (deselectionDate) => {
        dispatch(setDeselectionDate({ id, deselectionDate }));
    };

    const handleDateDeselection = (dateField) => {
        dispatchSetDeselectionDate(dateField);
    };

    const dateMetadata = metadataResponse?.metaDataDate?.map(
        (el) => el.name,
        []
    );

    const handleClearAllFilters = () => {
        dispatch(resetSelections({ id }));
    }

    useEffect(() => {
        const filterArray = Object?.entries(selections).reduce(
            (acc, [name, { type, values = [] }]) => {
                if (type === "number" && values.length > 0) {
                    const numValues = values.map(Number);
                    acc.push({
                        name,
                        minimum: Math.min(...numValues),
                        maximum: Math.max(...numValues),
                    });
                } else if (
                    type === "date" &&
                    values.minimum &&
                    values.maximum
                ) {
                    acc.push({
                        name,
                        minimum: new Date(values.minimum).toISOString(),
                        maximum: new Date(values.maximum).toISOString(),
                    });
                } else if (values.length > 0) {
                    acc.push({ name, values });
                }
                return acc;
            },
            []
        );
        if (topFilters) {
            topFilters.forEach((filter) => {
                if (dateMetadata.includes(filter.name)) {
                    filterArray.push(filter);
                }
            });
        }
        const areFiltersEqual =
            JSON.stringify(filterArray) === JSON.stringify(topFilters);
        if (!areFiltersEqual) {
            dispatch(setTopFilters({ id, filters: filterArray }));
        }
    }, [selections, topFilters, dateMetadata, dispatch, id]);

    const valuesFilterMetadata =
        metadataResponse?.metaData?.filter((item) => item.type !== "date") ||
        [];
    const datesFilterMetadata =
        metadataResponse?.metaData?.find((item) => item.type === "date") || [];

    return (
        <div className="filter-panel">
            <div className="filter-top">
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        Filters
                    </Title>
                    <Text>Applicable to all data</Text>
                </div>
            </div>
            <div className="filter-content">
                <Collapse
                    accordion
                    style={{
                        background: "none",
                        fontSize: token.fontSizeLG,
                        color: "#090A09",
                    }}
                    bordered={false}
                    defaultActiveKey={[]}
                    expandIcon={({ isActive }) =>
                        isActive ? <CollapseIcon /> : <ExpandIcon />
                    }
                    expandIconPosition="end"
                    items={[
                        {
                            key: "1",
                            label: (
                                <span
                                    style={{
                                        fontSize: token.fontSizeLG,
                                        fontWeight: "600",
                                        color: "#090A09",
                                    }}
                                >
                                    Values
                                </span>
                            ),
                            children: (
                                <ValuesFilterContent
                                    metadata={valuesFilterMetadata}
                                    handleFilter={handleFilter}
                                    selections={selections}
                                />
                            ),
                        },
                        {
                            key: "2",
                            label: (
                                <span
                                    style={{
                                        fontSize: token.fontSizeLG,
                                        fontWeight: "600",
                                        color: "#090A09",
                                    }}
                                >
                                    Dates
                                </span>
                            ),
                            children: (
                                <DatesFilterContent
                                    metadata={datesFilterMetadata}
                                />
                            ),
                        },
                        {
                            key: "3",
                            label: (
                                <span
                                    style={{
                                        fontSize: token.fontSizeLG,
                                        fontWeight: "600",
                                        color: "#090A09",
                                    }}
                                >
                                    Concepts
                                </span>
                            ),
                            children: (
                                <ConceptsFilterContent
                                    metadata={{
                                        concepts: {
                                            options: topOptionsConcepts,
                                            selected: topConcepts,
                                        },
                                        conceptList: {
                                            options: dataList,
                                            selected: activeConceptList,
                                        }
                                    }}
                                    // Select Concepts
                                    deselect={true}
                                    onConceptsChange={handleConceptsChange}
                                    onConceptsDeselection={dispatchSetDeselectionConcepts}

                                    // Concept Lists
                                    sentimentDisabled={sentimentDisabled}
                                    setConceptList={handleConceptListChange}
                                    conceptTypeDay={activeConceptList}
                                    handleChangeType={handleChangeConceptListType}
                                    setSelectedLocalConcepts={handleConceptsChange}
                                    resetType={() => dispatch(setTopConceptListDaylight(""))}
                                />
                            ),
                        },
                    ]}
                />
            </div>
            <div className="global-filter-bottom">
                <Button block={false} onClick={handleClearAllFilters}>Clear</Button>
            </div>
        </div>
    );
};
